import {Filters} from '../../global/filter-model';
import {Injectable} from '@angular/core';
import { TVDModel } from 'src/app/features/total-value-delivery/types/tvd-model';
import { AddedServiceMetric, ServiceInsightsPresentationData, ServiceInsightsPresentationDataObject } from 'src/app/features/service-insights/types/service-insight-types';
import { DatePipe } from '@angular/common'
import { availableBrandGroups, TVDMetricGroupResponse } from 'src/app/features/total-value-delivery/types/tvd-types';
import { ContractModel } from 'src/app/global/contract-filter-model';
import { PresentationService } from './presentation.service';
import { HttpClientService } from './http-client.service';
import { UserService } from './user.service';
import { FiltersService } from './filters.service';
import { AdvancedOneCustomerFilters } from 'src/app/config/GlobalCacheLoader.service';
import { TVDService } from 'src/app/features/total-value-delivery/services/tvd.service';
import { FilterRequests } from './filter-requests.service';
import { CurrencyUomService } from './currency-uom.service';
import { PresentationType } from '../types/presentation-types';
import { CostAssumptionsLayoutResolver } from 'src/app/features/total-value-delivery/resolvers/cost-assumptions-layout.resolver';
import { MetricCostAssumptionsPayloadBuilder } from 'src/app/features/total-value-delivery/builders/metric-cost-assumptions-payload.builder';

@Injectable({
  providedIn: 'root',
})
export class EbrService {
  private datePipe: DatePipe;
  controller = new AbortController();

  private customDateRange: string = '';
  private oneCustomerName: string = '';
  private advancedOneCustomerFilters : AdvancedOneCustomerFilters | undefined;
  private excludeDeletedAccounts: boolean = true;

  constructor(
    private _httpClientService: HttpClientService,
    private _userService: UserService,
    private _presentationService: PresentationService,
    private _filterService: FiltersService,
    private _tvdService: TVDService,
    private _filterRequest: FilterRequests,
    private _currencyUomService: CurrencyUomService,
    private _costAssumptionsLayoutResolver: CostAssumptionsLayoutResolver,
    private _metricCostAssumptionsPayloadBuilder: MetricCostAssumptionsPayloadBuilder
  ) {
    this.datePipe = new DatePipe('en-US');
    this._filterService.customDateRangeObservable.subscribe(f => this.customDateRange = `${f.startDate.formatted}:${f.endDate.formatted}`);
    this._filterService.oneCustomerNameObservable.subscribe(f => this.oneCustomerName = f);
    this._filterService.oneCustomerFiltersObservable.subscribe(f => this.advancedOneCustomerFilters = f);
    this._filterRequest.excludeDeletedAccountsObservable.subscribe(f => this.excludeDeletedAccounts = f);
  }

  async getExcelReport(filterRequest: ContractModel) {
    let url = `/excel-download/getExcelReport?region=${filterRequest.regions}&countries=${filterRequest.countries}&period=${filterRequest.period}`;
    url = this.appendCustomDateRangeToUrl(url, filterRequest.period);

    const payload = {
      division: filterRequest?.division,
      dataView: filterRequest?.dataView,
      topLevelContractGroup: filterRequest?.topLevelContractGroup,
      secondLevelContractFilter: filterRequest?.secondLevelContractFilter,
      thirdLevelContractFilter: filterRequest?.thirdLevelContractFilter,
      fourthLevelContractFilter: filterRequest?.fourthLevelContractFilter,
      accts: filterRequest?.accts,
      oneCustomerName: this.oneCustomerName,
      oneCustomerFilters: this.advancedOneCustomerFilters,
      excludeDeletedAccounts: this.excludeDeletedAccounts
    };

    return await this._httpClientService.post(
      url,
      payload,
      'blob'
    );
  }

  async getPresentation (filters: ContractModel, tvdRequest : TVDModel, selectedServiceMetrics : AddedServiceMetric[] = [], addedTopBottomLocs: Record<string, boolean>, serviceInsightsPresentationData:ServiceInsightsPresentationData,isMultiRegional: boolean) {
    let data = this._constructPresentationData(filters, tvdRequest, selectedServiceMetrics, addedTopBottomLocs, serviceInsightsPresentationData, isMultiRegional);

    var presentationType = this._presentationService.getSelectedPresentationTypeCode();

    const translationParam = (filters.translation != undefined && filters.translation.length > 0) ? `&translation=${filters.translation}` : '';

    let url = `/powerpoint/${presentationType}?region=${filters.regions}&countries=${filters.countries}&period=${filters.period}${translationParam}`;
    url = this.appendCustomDateRangeToUrl(url, filters.period);

    return await this._httpClientService.post(
      url,
      data,
      'blob'
    );
  }

  async getDistributorPresentation(filters: ContractModel, tvdMetricSelections: TVDMetricGroupResponse[], selectedBrandGroup:availableBrandGroups, selectedBrandFilterOptions: { [id: string]: string[] }) {
    tvdMetricSelections = this.applyMetricGroupModifiedContributions(tvdMetricSelections);
    let body = this._constructDistributorPresentationData(filters, tvdMetricSelections, selectedBrandGroup, selectedBrandFilterOptions);
    var presentationType = PresentationType.Distributor; //currently, this only type of ppt for distributor

    let url = `/powerpoint/${presentationType}?region=${filters.regions}&countries=${filters.countries}&period=${filters.period}&selectedProgram=All`;
    url = this.appendCustomDateRangeToUrl(url, filters.period);

    return await this._httpClientService.post(
      url,
      body,
      'blob'
    );
  }

  async getPestPresentation(tvdRequest : TVDModel,filters: ContractModel, selectedServiceMetrics : AddedServiceMetric[] = []) {
    // All custom metrics are included by default, if user manually adds metrics, only those will be included
    let customMetricsData = tvdRequest?.addedMetrics ?? []
    var presentationType = PresentationType.PestTVD; //currently, this only type of ppt for pest
    let layout = this._costAssumptionsLayoutResolver.resolveFromFilters(filters);
    let metricCostAssumptionsPayload = this._metricCostAssumptionsPayloadBuilder.build(
      this._tvdService.getFormData(),
      layout,
    );
    const tvdMetricSelections = this.applyMetricGroupModifiedContributions(tvdRequest.tvdMetricSelections);

    const payload = {
      "contractModel": {
        "division" : filters.division,
        "dataView" : filters.dataView,
        "topLevelContractGroup": filters.topLevelContractGroup,
        "secondLevelContractFilter": filters.secondLevelContractFilter,
        "thirdLevelContractFilter": filters.thirdLevelContractFilter,
        "accts": filters.accts,
        "oneCustomerName": this.oneCustomerName,
        "oneCustomerFilters": this.advancedOneCustomerFilters,
        "excludeDeletedAccounts": this.excludeDeletedAccounts,
        "currencyCode": this._currencyUomService.getCurrency(),
        "unitOfMeasure": this._currencyUomService.getUom()
      },
      "tvdModel": {
        tvdMetrics: tvdMetricSelections,
        customMetrics: customMetricsData
      },
      "mainDashDataModel": {
        "selectedMetrics": selectedServiceMetrics
      },
      "clientDateTime": this.datePipe.transform(new Date(), 'dd MMMM yyyy'),
      "costAssumptionsMetrics": metricCostAssumptionsPayload
    }

    let url = `/powerpoint/${presentationType}?region=${filters.regions}&countries=${filters.countries}&period=${filters.period}&selectedProgram=All`;
    url = this.appendCustomDateRangeToUrl(url, filters.period);

    return await this._httpClientService.post(
      url,
      payload,
      'blob'
    );
  }

  private _constructPresentationData(filters: ContractModel, tvdRequest : TVDModel, selectedServiceMetrics: AddedServiceMetric[] = [], addedTopBottomLocs: Record<string, boolean>, serviceInsightsPresentationData: ServiceInsightsPresentationData,isMultiRegional: boolean) {
    let layout = this._costAssumptionsLayoutResolver.resolveFromFilters(filters);
    let metricCostAssumptionsPayload = this._metricCostAssumptionsPayloadBuilder.build(
      this._tvdService.getFormData(),
      layout,
    );
    const tvdMetricSelections = this.applyMetricGroupModifiedContributions(tvdRequest.tvdMetricSelections);

    const payload = {
      // TODO : Building payload explicitly for contract model can lead to inconsistencies or missed attributes. 
      // Consider using a subject to keep track of the common payload for all requests.
      "contractModel": {
        "division" : filters.division,
        "dataView" : filters.dataView,
        "topLevelContractGroup": filters.topLevelContractGroup,
        "secondLevelContractFilter": filters.secondLevelContractFilter,
        "accts": filters.accts,
        "oneCustomerName": this.oneCustomerName,
        "oneCustomerFilters": this.advancedOneCustomerFilters,
        "excludeDeletedAccounts": this.excludeDeletedAccounts,
        "currencyCode": this._currencyUomService.getCurrency(),
        "unitOfMeasure": this._currencyUomService.getUom()
      },
      "tvdModel": {
        tvdMetrics: tvdMetricSelections,
        customMetrics: tvdRequest.addedMetrics
      },
      "mainDashDataModel": {
        "selectedMetrics": selectedServiceMetrics
      },
      "addedTopBottomLocs": addedTopBottomLocs,
      "addedFindings": serviceInsightsPresentationData,
      "clientDateTime": this.datePipe.transform(new Date(), 'dd MMMM yyyy'),
      "costAssumptionsMetrics": metricCostAssumptionsPayload,
      "isMultiRegional": isMultiRegional
    }

    return payload;
  }

  private _constructDistributorPresentationData(filters: ContractModel, tvdMetricSelections: TVDMetricGroupResponse[], selectedBrandGroup: availableBrandGroups, selectedBrandFilterOptions: { [id: string]: string[] }) {

    const body = {
      "contractModel": { ...filters, currencyCode: this._currencyUomService.getCurrency(), unitOfMeasure: this._currencyUomService.getUom() },
      "tvdModel": {
        tvdMetrics: tvdMetricSelections,
        customMetrics: []
      },
      "brandGroup":selectedBrandGroup,
      "brands": selectedBrandFilterOptions[availableBrandGroups.DISTRIBUTOR], //only need to send Distributor brands for now
      "clientDateTime": this.datePipe.transform(new Date(), 'dd MMMM yyyy')
    }

    return body;
  }

  // Convert all custom metrics to the format required for the PPT download
  private getAllCustomMetricsData(tvdRequest: TVDModel) {
    let customMetrics = Array.from(tvdRequest.custTileDict?.values() ?? []).map((item) => {
      return {
        Title: item.title,
        Cost: item.cost,
        ValueUnit: item.unit,
        Value: item.metricValue,
        ValuePillar: item.valuePillar.replace(/\s+/g, ''), //remove spaces so enums can map to the enums in API
        ImpactCategory: item.impactCategory.replace(/\s+/g, ''), //remove spaces so enums can map to the enums in API
      };
    });

    return customMetrics;
  }

  async getSmartpowerSummary(username: string | null, request: Filters) {
    if (username == null) {
      throw new Error("No userID found");
    }

    const data = {
      "h5LevelCtrGrp": request?.topLevelHierarchies?.map(t => t.h5Number),
      "h7LevelCtrGrp": request?.h7Agreements?.map(h => h.number),
      "accts": request?.customers?.map(c => c.accountNumber)
    }

    let url = `/smartpower/getSummaryPage?region=${request?.regions}&countries=${request?.countries}&period=${request?.period}&email=${username}`;
    url = this.appendCustomDateRangeToUrl(url, request?.period);

    return await this._httpClientService.post(
      url,
      data
    );
  }

  async getSmartpowerLocation(request: Filters, metric: string) {
    const data = {
      "h5LevelCtrGrp": request?.topLevelHierarchies?.map(t => t.h5Number),
      "h7LevelCtrGrp": request?.h7Agreements?.map(h => h.number),
      "accts": request?.customers?.map(c => c.accountNumber)
    }

    let url = `/smartpower/getLocationSummary/${metric}?region=${request?.regions}&countries=${request?.countries}&period=${request?.period}`;
    url = this.appendCustomDateRangeToUrl(url, request?.period);

    return await this._httpClientService.post(
      url,
      data
    );
  }


  async captureUserInsights(filters: ContractModel, itemsToCapture:ServiceInsightsPresentationDataObject[]) {
    const username = this._userService.getUsername();
    if (username == null) {
      throw new Error("No userID found");
    }

    const data = {
      "ctrAcctModel": {
        "h5LevelCtrGrp": filters.topLevelContractGroup,
        "h7LevelCtrGrp": filters.secondLevelContractFilter,
        "accts": filters.accts
      },
      "email": username,
      "userServiceInsightModel": itemsToCapture
    }

    let url = `/service-insights/service-insight/save-insight?region=${filters?.regions}&countries=${filters?.countries}&period=${filters?.period}`;
    url = this.appendCustomDateRangeToUrl(url, filters.period);

    return await this._httpClientService.post(
      url,
      data
    );
  }

  private appendCustomDateRangeToUrl(url: string, periodSelected:string | undefined): string {
    if (periodSelected === 'Custom') {
      return `${url}&dateRange=${this.customDateRange}`;
    }
    return url;
  }

  private applyMetricGroupModifiedContributions(tvdMetricSelections: TVDMetricGroupResponse[]): TVDMetricGroupResponse[] {
    return tvdMetricSelections.map(metricGroup => ({
      ...metricGroup,
      indvMetrics: metricGroup.indvMetrics?.map(metric =>
        this.applyMetricModifiedContributions(metric)
      ),
      summMetrics: metricGroup.summMetrics?.map(metric =>
        this.applyMetricModifiedContributions(metric)
      )
    }));
  }

  private applyMetricModifiedContributions(metric: any): any {
    const updatedMetric = { ...metric };

    // Apply modifiedValueContribution if available and greater than 0
    const modifiedValueContribution = parseFloat(metric.modifiedValueContribution?.replace(/,/g, '') ?? '0');
    if (modifiedValueContribution > 0) {
      updatedMetric.valueContribution = metric.modifiedValueContribution!;
      updatedMetric.modifiedValueContribution = '0';
    }

    // Apply modifiedMonetaryContribution if available and greater than 0
    const modifiedMonetaryContribution = parseFloat(metric.modifiedMonetaryContribution?.replace(/,/g, '') ?? '0');
    if (modifiedMonetaryContribution > 0) {
      updatedMetric.monetaryContribution = metric.modifiedMonetaryContribution!;
      updatedMetric.modifiedMonetaryContribution = '0';
    }

    return updatedMetric;
  }
}

export default EbrService;
